import React, { useCallback, useEffect } from "react";
import PathPickFilterPanel from "@/features/pathpick/components/ui/PathPickFilterPanel";
import PathPickRangeFilterSection, {
  usePathPickRangeFilterChips,
} from "@/features/pathpick/components/ui/PathPickRangeFilterSection";
import usePathPickBasketFilter, {
  PathPickBasketFilter,
  PathPickBasketFilterKey,
} from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import usePathPickBasketFilterGroups, {
  getPathPickBasketFilterValues,
  togglePathPickBasketFilterValue,
} from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilterGroups";
import usePathPickFilterDraft from "@/features/pathpick/hooks/usePathPickFilterDraft";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import {
  getInvalidPathPickRangeFields,
  PathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// 담은 상품 리스트 필터 — 탭별 그룹 구성은 usePathPickBasketFilterGroups가 정한다.
// 모든 탭에 마진·거래수 범위가 붙고, 패널 안 변경은 `필터 적용`을 눌러야 목록에 반영된다.
function PathPickBasketListFilter({ isOpen, onClose }: Props) {
  const { filter, tab, apply, clearFilters, removeValue } =
    usePathPickBasketFilter();
  const groups = usePathPickBasketFilterGroups();
  // 범위 필터는 크림·포이즌 비교 값으로 거르는 조건이라 비교 표시를 끄면 함께 감춘다.
  const { isComparisonVisible } = usePathPickProductCardView();
  const { draft, setDraftValues, updateDraft, discard } =
    usePathPickFilterDraft<PathPickBasketFilter>(filter);

  // 패널이 닫히거나(취소·필터 버튼) 탭이 바뀌면(탭마다 필터 구성이 다르다)
  // 적용하지 않은 수정은 버린다.
  useEffect(() => {
    if (!isOpen) {
      discard();
    }
  }, [isOpen, discard]);

  useEffect(() => {
    discard();
  }, [tab, discard]);

  const getSelectedValues = (key: PathPickBasketFilterKey) =>
    getPathPickBasketFilterValues(draft, key);

  const getAppliedValues = (key: PathPickBasketFilterKey) =>
    getPathPickBasketFilterValues(filter, key);

  // 드래프트를 클로저로 읽지 않고 updateDraft로 최신 값을 받아 참조를 고정한다.
  const handleToggle = useCallback(
    (key: PathPickBasketFilterKey, value: string | number) => {
      updateDraft((current) =>
        togglePathPickBasketFilterValue(current, key, value)
      );
    },
    [updateDraft]
  );

  // 범위 칩 삭제는 다른 칩처럼 즉시 반영한다.
  const handleRemoveRange = useCallback(
    (patch: PathPickRangeFilter) => {
      apply({ ...filter, ...patch });
    },
    [filter, apply]
  );
  const rangeChips = usePathPickRangeFilterChips(filter, handleRemoveRange);

  // 적용 후에도 패널은 열어 둔다 — 적용값이 드래프트와 같아지므로 덮어쓴 값만 비운다.
  const handleApply = () => {
    apply(draft);
    discard();
  };

  return (
    <PathPickFilterPanel
      isOpen={isOpen}
      groups={groups}
      getSelectedValues={getSelectedValues}
      getAppliedValues={getAppliedValues}
      onToggle={handleToggle}
      onRemoveChip={removeValue}
      onClearAll={clearFilters}
      onApply={handleApply}
      onCancel={onClose}
      isApplyDisabled={getInvalidPathPickRangeFields(draft).length > 0}
      extraCells={
        isComparisonVisible ? (
          <PathPickRangeFilterSection value={draft} onChange={setDraftValues} />
        ) : undefined
      }
      extraChips={rangeChips}
    />
  );
}

// 헤더는 선택이 바뀔 때마다 리렌더되므로 memo로 끊는다 — prop은 isOpen·onClose(고정 참조)뿐이고
// 나머지는 자체 훅(쿼리스트링·filter-info)에서 읽어, 선택 변경에는 다시 그릴 이유가 없다.
export default React.memo(PathPickBasketListFilter);
